import { Box, Button, Stack } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import PopupMap from '/@/components/ask-an-expert/popup-map';
import FieldCard from '/@/components/crop-monitoring/field-card';
import Drawing from '/@/components/drawing';
import Icon from '/@/components/icon';
import Card from '/@/components/new-home/card';
import Popover from '/@/components/popover';
import settings from '/@/core/settings';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useMyFieldsInfinite } from '/@/hooks/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import Spinner from '../../spinner';
import styles from '../styles.module.css';

const MobileFieldStepper = ({
    onClick,
    selected,
    disablePointFields = false,
    checkbox,
    handleChange,
    selectedFeature,
}) => {
    const history = useNavigate();
    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const [searchText, setSearchText] = useState('');
    const country = useSelector(getSessionCountry);
    const [field, setField] = useState(undefined);
    const dispatch = useDispatch();
    const setSelectedFeatureDispatch = bindActionCreators(setSelectedFeature, dispatch);

    // New field
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId');
    const ownNodeQuery = useOwnUserNode();
    const handleClickCard = () => {
        if (settings.app.theme === 'expertobayer') {
            setNewFieldModalOpen(true);
        } else {
            history(reverse('fields.NewField'));
            setSelectedFeatureDispatch({ view: 'fields.NewField', selectedFeature: 'cropMonitoring' });
        }
    };

    const [infiniteTriggerRef, setInfiniteTriggerRef] = useState(null);
    const debouncedSearchText = useDebounce(searchText, 1000);
    const fieldsQuery = useMyFieldsInfinite({
        name: debouncedSearchText,
        country: country.code,
        pageSize: 5,
        geometryType: checkbox.poligons == checkbox.points ? undefined : checkbox.poligons ? 'polygon' : 'point',
    });
    const fields = fieldsQuery.data?.flatMap((el) => el.results) || [];

    const handleApply = () => {
        handleClose();
    };

    useEffect(() => {
        if (!infiniteTriggerRef) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !fieldsQuery.isValidating) {
                        fieldsQuery.setSize(fieldsQuery.size + 1);
                    }
                });
            },
            {
                threshold: 0.2,
            }
        );
        observer.observe(infiniteTriggerRef);
        return () => {
            if (infiniteTriggerRef) observer.unobserve(infiniteTriggerRef);
            observer.disconnect();
        };
    }, [fieldsQuery, infiniteTriggerRef]);

    return (
        <Fragment>
            <PopupMap
                visible={Boolean(field)}
                onClose={() => setField(undefined)}
                fields={[field]}
                zoomPosition='BOTTOM_RIGHT'
                country={country}
                field={field}
            />
            <Grid container justifyContent='center' spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} sx={{ pt: 2 }}>
                    <Card
                        maxWidth
                        icon='plus'
                        iconElement={<Icon type='newField' className={styles.iconCardNewField} />}
                        name={_('Crear un nuevo lote')}
                        direction='row'
                        color={settings.app.theme === 'expertobayer' ? 'expertobayer' : 'green-light'}
                        bolder
                        center
                        shadow={false}
                        handleClick={handleClickCard}
                        border={false}
                    />
                </Grid>

                <Fragment>
                    <Grid item container alignItems='center' justifyContent='space-between'>
                        <Grid item xs={disablePointFields ? 10 : 12}>
                            <Input
                                color={settings.app.theme}
                                value={searchText}
                                onChange={setSearchText}
                                placeholder={_('Buscar por Nombre, Ubicación...')}
                            />
                        </Grid>
                        {disablePointFields && (
                            <Grid item sx={{ height: '100%' }}>
                                <Button sx={{ height: '100%' }} onClick={handleClick}>
                                    <span className='material-icons'>tune</span>
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                    {fields.length > 0 && (
                        <Fragment>
                            {fields.map((f) => {
                                return (
                                    <Grid item xs={12}>
                                        <FieldCard
                                            setField={setField}
                                            selected={selected}
                                            onClick={onClick}
                                            key={f.id}
                                            field={f}
                                            disabled={disablePointFields && f.geometry.length === 1}
                                        />
                                    </Grid>
                                );
                            })}
                        </Fragment>
                    )}
                    {!fieldsQuery.isValidating && fields.length == 0 && (
                        <Grid sx={{ py: 6 }}>
                            <Drawing
                                size='sm'
                                type='library-not-found'
                                title={_('No se encontraron resultados para tu búsqueda.')}
                            />
                        </Grid>
                    )}
                    <Box height={'10px'} ref={setInfiniteTriggerRef} />
                    {fieldsQuery.isValidating && (
                        <Stack justifyContent='center' alignItems='center'>
                            <Spinner />
                        </Stack>
                    )}
                </Fragment>
            </Grid>
            <Popover open={open} handleClose={handleClose} title={_('Mostrar lotes')}>
                <Grid item>
                    <FormControlLabel
                        control={
                            <Checkbox
                                defaultChecked
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                name='poligons'
                                checked={checkbox.poligons}
                            />
                        }
                        label={_('Dibujados con polígonos (válidos)')}
                        sx={{
                            fontSize: 14,
                            color: (t) => t.palette.primary.main,
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                defaultChecked
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                name='points'
                                checked={checkbox.points}
                            />
                        }
                        label={_('Marcados con un punto (No válidos)')}
                        sx={{
                            fontSize: 14,
                            color: (t) => t.palette.primary.main,
                        }}
                    />
                </Grid>
                <Grid item container direction='column' sx={{ gap: 1 }}>
                    <Grid item>
                        <Button variant='contained' onClick={handleApply}>
                            {_('Aplicar')}
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
            {ownNodeQuery.data?.id && (
                <NewFieldGrowersModal
                    open={newFieldModalOpen}
                    setOpen={setNewFieldModalOpen}
                    growerUserNodeId={userNodeId ? Number(userNodeId) : ownNodeQuery.data?.id}
                    trackEntrySource={
                        selectedFeature === 'newNitrogenRecommendationForm'
                            ? 'n-smart-recommendation'
                            : 'crop-monitoring'
                    }
                />
            )}
        </Fragment>
    );
};

export default MobileFieldStepper;
